import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Alert,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Container
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  Token as TokenIcon,
  Security as SecurityIcon,
  Launch as LaunchIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { TENANT } from '../config';

// Contract addresses on Ethereum Mainnet
const CONTRACTS = {
  PropertyToken: '0x8b33a83b2d6e3BC628DAe886E5215a54011C11ca',
  StakingVault: '0xd7c2C144518d60f8A16BA6E0e802b8afF495EAd8'
};

const ETHERSCAN_BASE = 'https://etherscan.io/address/';
const NETWORK = 'Ethereum Mainnet';
const CHAIN_ID = 1;

export default function Blockchain() {
  const [connected, setConnected] = useState(false);
  const [account, setAccount] = useState<string | null>(null);
  const [chainId, setChainId] = useState<number | null>(null);

  // Check if MetaMask is installed and connected
  useEffect(() => {
    const checkConnection = async () => {
      if (typeof window.ethereum !== 'undefined') {
        try {
          const accounts = await window.ethereum.request({ method: 'eth_accounts' });
          const currentChainId = await window.ethereum.request({ method: 'eth_chainId' });
          
          if (accounts.length > 0) {
            setAccount(accounts[0]);
            setConnected(true);
            setChainId(parseInt(currentChainId, 16));
          }
        } catch (error) {
          console.error('Error checking connection:', error);
        }
      }
    };

    checkConnection();

    // Listen for account changes
    if (window.ethereum && window.ethereum.on) {
      window.ethereum.on('accountsChanged', (accounts: string[]) => {
        if (accounts.length > 0) {
          setAccount(accounts[0]);
          setConnected(true);
        } else {
          setAccount(null);
          setConnected(false);
        }
      });

      window.ethereum.on('chainChanged', (chainId: string) => {
        setChainId(parseInt(chainId, 16));
      });
    }

    return () => {
      if (window.ethereum && window.ethereum.removeListener) {
        window.ethereum.removeListener('accountsChanged', () => {});
        window.ethereum.removeListener('chainChanged', () => {});
      }
    };
  }, []);

  const connectWallet = async () => {
    if (typeof window.ethereum !== 'undefined') {
      try {
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        setAccount(accounts[0]);
        setConnected(true);
      } catch (error) {
        console.error('Error connecting wallet:', error);
      }
    } else {
      alert('MetaMask not detected. Please install MetaMask to interact with the blockchain.');
    }
  };

  const switchToMainnet = async () => {
    if (typeof window.ethereum !== 'undefined') {
      try {
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: '0x1' }], // Mainnet
        });
      } catch (error) {
        console.error('Error switching network:', error);
      }
    }
  };

  const addTokenToWallet = async () => {
    if (typeof window.ethereum !== 'undefined') {
      try {
        const tokenImageUrl = `${window.location.origin}/Primary.png`;
        await window.ethereum.request({
          method: 'wallet_watchAsset',
          params: [{
            type: 'ERC20',
            options: {
              address: CONTRACTS.PropertyToken,
              symbol: 'HSPROP',
              decimals: 0,
              image: tokenImageUrl,
            },
          }],
        });
      } catch (error) {
        console.error('Error adding token to wallet:', error);
      }
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom align="center">
          🔗 Blockchain Integration
        </Typography>
        
        <Typography variant="h6" color="text.secondary" align="center" paragraph>
          {TENANT.branding.name} smart contracts are now live on Ethereum Mainnet
        </Typography>

        {/* Connection Status */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <WalletIcon color={connected ? 'success' : 'disabled'} />
                  <Box>
                    <Typography variant="h6">
                      Wallet {connected ? 'Connected' : 'Not Connected'}
                    </Typography>
                    {account && (
                      <Typography variant="body2" color="text.secondary">
                        {account.slice(0, 6)}...{account.slice(-4)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {!connected && (
                    <Button variant="contained" onClick={connectWallet}>
                      Connect Wallet
                    </Button>
                  )}
                  
                  {connected && chainId !== CHAIN_ID && (
                    <Button variant="outlined" onClick={switchToMainnet}>
                      Switch to Mainnet
                    </Button>
                  )}
                  
                  {connected && chainId === CHAIN_ID && (
                    <Chip 
                      icon={<CheckIcon />} 
                      label="Mainnet Connected" 
                      color="success" 
                    />
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {chainId && chainId !== CHAIN_ID && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            You're connected to the wrong network. Please switch to Ethereum Mainnet to interact with the contracts.
          </Alert>
        )}

        {/* Contract Information */}
        <Grid container spacing={3}>
          {/* Property Token Contract */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <TokenIcon color="primary" fontSize="large" />
                  <Typography variant="h5">Property Token</Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  ERC-1155 tokens representing fractional ownership of real estate properties.
                </Typography>
                
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Fractional Property Ownership" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Transfer & Trade Tokens" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Dividend Distribution" />
                  </ListItem>
                </List>

                <Divider sx={{ my: 2 }} />
                
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                  Contract: {CONTRACTS.PropertyToken}
                </Typography>
                
                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    startIcon={<LaunchIcon />}
                    href={`${ETHERSCAN_BASE}${CONTRACTS.PropertyToken}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View on Etherscan
                  </Button>
                  
                  {connected && chainId === CHAIN_ID && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={addTokenToWallet}
                    >
                      Add to Wallet
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Staking Vault Contract */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <SecurityIcon color="primary" fontSize="large" />
                  <Typography variant="h5">Staking Vault</Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  Secure staking mechanism for earning rewards on property token holdings.
                </Typography>
                
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Stake Property Tokens" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Earn Staking Rewards" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Compound Interest" />
                  </ListItem>
                </List>

                <Divider sx={{ my: 2 }} />
                
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                  Contract: {CONTRACTS.StakingVault}
                </Typography>
                
                <Box sx={{ mt: 2 }}>
                  <Button
                    size="small"
                    startIcon={<LaunchIcon />}
                    href={`${ETHERSCAN_BASE}${CONTRACTS.StakingVault}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View on Etherscan
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Network Information */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Network Information
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Network</Typography>
                <Typography variant="body1">{NETWORK}</Typography>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Chain ID</Typography>
                <Typography variant="body1">{CHAIN_ID}</Typography>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Deployment Date</Typography>
                <Typography variant="body1">September 28, 2025</Typography>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Gas Used</Typography>
                <Typography variant="body1">~0.006 ETH</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Coming Soon Features */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              🚀 Coming Soon
            </Typography>
            
            <Typography variant="body2" color="text.secondary" paragraph>
              Additional blockchain features currently in development:
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <List dense>
                  <ListItem>
                    <ListItemText primary="• Property Token Minting Interface" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Staking Dashboard" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Dividend Claiming Portal" />
                  </ListItem>
                </List>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <List dense>
                  <ListItem>
                    <ListItemText primary="• Secondary Market Trading" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Governance Voting" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Multi-Chain Support" />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}


